var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Redirect, useHistory } from 'react-router-dom';
import { useStripes } from '@folio/stripes/core';
import { Button, Datepicker, Icon, Pane, Paneset, Select, } from '@folio/stripes/components';
import { RecordType } from '../types/api';
import { TableErrorState, TableLoadingSkeleton } from '../components/TableStates';
import ProposalReportSheet from '../components/ProposalReport/ProposalReportSheet';
import usePendingApprovalReport from '../hooks/usePendingApprovalReport';
import useOrganizationNames from '../hooks/useOrganizationNames';
import useUserNames from '../hooks/useUserNames';
import { MEETING_REPORT_CAPABILITY } from '../utils/meetingReportAccess';
import { defaultReportPeriod, toPeriodBounds } from '../utils/reportPeriod';
import css from './ProposalsReport.css';
var ProposalsReport = function (_a) {
    var listPath = _a.listPath;
    var intl = useIntl();
    var history = useHistory();
    /*
     * `isLoading` is read, not discarded: the library names come from a SEPARATE query
     * (mod-czna-users, 5-minute staleTime), so pressing Tisk before it settles prints raw
     * UUIDs in the Knihovna column — visibly wrong on paper, and not obviously our bug to
     * whoever is holding it. Blind review, finding 9.
     */
    var _b = useOrganizationNames(), getOrganizationName = _b.getOrganizationName, organizationsLoading = _b.isLoading;
    var _c = useState(function () { return defaultReportPeriod(); }), period = _c[0], setPeriod = _c[1];
    var _d = useState(''), recordType = _d[0], setRecordType = _d[1];
    var bounds = useMemo(function () { return toPeriodBounds(period); }, [period]);
    var params = useMemo(function () { return (bounds ? __assign(__assign({}, bounds), { recordType: recordType || null }) : null); }, [bounds, recordType]);
    var _f = usePendingApprovalReport(params), report = _f.report, isLoading = _f.isLoading, isFetching = _f.isFetching, isError = _f.isError, error = _f.error, refetch = _f.refetch;
    // Same reasoning as organizationsLoading above: creator names come from their own
    // batched query, built from exactly the ids this report's items carry.
    var creatorIds = useMemo(function () { var _a; return ((_a = report === null || report === void 0 ? void 0 : report.items) !== null && _a !== void 0 ? _a : []).map(function (item) { return item.createdBy; }); }, [report]);
    var _g = useUserNames(creatorIds), getUserName = _g.getUserName, usersLoading = _g.isLoading;
    var typeOptions = useMemo(function () { return __spreadArray([
        {
            value: '',
            label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.filter.type.all' }),
        }
    ], Object.values(RecordType).map(function (type) { return ({
        value: type,
        label: intl.formatMessage({ id: "ui-czna-authorities.recordType.".concat(type) }),
    }); }), true); }, [intl]);
    // Stripes Datepicker onChange args: (event, displayValue, isoBackendValue). The ISO
    // value is empty when the field is cleared, which `toPeriodBounds` then reports as an
    // unusable period rather than silently widening the window.
    var onDateChange = useCallback(function (key) {
        return function (_e, _displayValue, isoBackendValue) {
            return setPeriod(function (current) {
                var _a;
                return (__assign(__assign({}, current), (_a = {}, _a[key] = isoBackendValue || '', _a)));
            });
        };
    }, []);
    // On screen, each section is paginated (ProposalReportSheet, PAGE_SIZE) so 1000 cards
    // don't all render at once. The printed sheet still needs every row regardless of which
    // page was showing, so this flips on right before print and back off once the print
    // dialog closes — `afterprint` fires whether the user printed or cancelled.
    var _h = useState(false), renderAllForPrint = _h[0], setRenderAllForPrint = _h[1];
    useEffect(function () {
        var onAfterPrint = function () { return setRenderAllForPrint(false); };
        window.addEventListener('afterprint', onAfterPrint);
        return function () { return window.removeEventListener('afterprint', onAfterPrint); };
    }, []);
    var handlePrint = useCallback(function () {
        // window.print() is synchronous, so the setState above must be committed to the DOM
        // BEFORE it runs — an ordinary setState only schedules that for later in the event.
        flushSync(function () { return setRenderAllForPrint(true); });
        window.print();
    }, []);
    var renderBody = function () {
        if (!bounds) {
            return (React.createElement("p", { className: css.notice, "data-testid": "report-invalid-period" },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.invalidPeriod" })));
        }
        // CZNA-538's lesson, applied here: a refused or failed query must not render as
        // "nothing to approve in this period".
        if (isError) {
            return React.createElement(TableErrorState, { error: error, onRetry: refetch, testId: "report-error" });
        }
        if (!report && (isLoading || isFetching)) {
            return React.createElement(TableLoadingSkeleton, { testId: "report-loading", columns: [2, 3, 9, 3, 3, 3] });
        }
        if (!report) {
            return null;
        }
        return (React.createElement(ProposalReportSheet, { report: report, getOrganizationName: getOrganizationName, getUserName: getUserName, renderAllForPrint: renderAllForPrint }));
    };
    /**
     * The controls are `noPrint`, the sheet is not. Printing the date pickers and the
     * "Tisk" button onto the meeting agenda would be the giveaway that this is a screenshot
     * of an app rather than a document.
     */
    var controls = (React.createElement("div", { className: css.controls, "data-testid": "report-controls" },
        React.createElement("div", { className: css.control },
            React.createElement(Datepicker, { value: period.from, onChange: onDateChange('from'), label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.report.from' }), backendDateStandard: "YYYY-MM-DD" })),
        React.createElement("div", { className: css.control },
            React.createElement(Datepicker, { value: period.to, onChange: onDateChange('to'), label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.report.to' }), backendDateStandard: "YYYY-MM-DD" })),
        React.createElement("div", { className: css.control },
            React.createElement(Select, { label: intl.formatMessage({ id: 'ui-czna-authorities.proposals.filter.type' }), value: recordType, dataOptions: typeOptions, onChange: function (e) {
                    return setRecordType(e.target.value || '');
                } }))));
    var printButton = (React.createElement(Button, { buttonStyle: "primary", marginBottom0: true, disabled: !report || organizationsLoading || usersLoading, onClick: handlePrint },
        React.createElement(Icon, { icon: "print" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.print" }))));
    return (React.createElement(Paneset, null,
        React.createElement(Pane, { defaultWidth: "fill", paneTitle: React.createElement(FormattedMessage, { id: "ui-czna-authorities.proposals.report.title" }), dismissible: true, onClose: function () { return history.push(listPath); }, lastMenu: printButton },
            React.createElement("div", { className: css.noPrint }, controls),
            renderBody())));
};
/**
 * CZNA-1510 — the report is a reviewer's tool, and its two routes are reachable by URL.
 * Without the capability the query would answer 403; a cataloguer who typed the address is
 * sent back to the list instead of being shown an error about a page that is not theirs.
 */
var ProposalsReportGate = function (props) {
    var stripes = useStripes();
    if (!stripes.hasPerm(MEETING_REPORT_CAPABILITY)) {
        return React.createElement(Redirect, { to: props.listPath });
    }
    return React.createElement(ProposalsReport, __assign({}, props));
};
export default ProposalsReportGate;
